import * as React from 'react';
import { CloseLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import { TOAST_EVENT_NAME, type ToastEvent } from '../lib/toast';

import { ToastIcon } from './toast';

/**
 * Mount once at the root of an app. Listens to the global toast events fired by
 * the `toast` API in `lib/toast` and renders them as a stack pinned to the
 * TOP-RIGHT corner, fixed to the viewport so they stay in view regardless of
 * page scroll. Each toast auto-dismisses after its duration (default 5s),
 * pauses while hovered, and persists if it carries an action. Click × to
 * dismiss immediately.
 *
 * (The previous implementation rendered the legacy `<Toast>` primitives as
 * inline siblings of an empty viewport — `ToastProvider` is a passthrough — so
 * toasts landed in normal document flow at the bottom-left and never dismissed,
 * because that primitive ignores `duration`/`onOpenChange`.)
 */

const DEFAULT_DURATION = 5000;

function ToastCard({
  event,
  onDismiss,
}: {
  event: ToastEvent;
  onDismiss: (id: string) => void;
}) {
  const { id, variant, message, title, action, duration = DEFAULT_DURATION } = event;
  const hasAction = Boolean(action);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTimer = React.useCallback(() => {
    if (hasAction) return; // persists while it offers an action
    timerRef.current = setTimeout(() => onDismiss(id), duration);
  }, [id, duration, hasAction, onDismiss]);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
      className={cn(
        'pointer-events-auto flex w-[min(420px,calc(100vw-2rem))] items-start gap-3',
        'bg-[var(--tvl-ink)] text-[var(--tvl-raised)]',
        'rounded-[var(--tvl-r3)] shadow-[var(--tvl-e2)] px-4 py-3',
        'animate-[toast-enter_var(--tvl-d-slow)_var(--tvl-ease-out)_forwards]',
      )}
    >
      <ToastIcon variant={variant} />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title ? <p className="text-sm font-semibold leading-tight">{title}</p> : null}
        <p className="text-sm leading-snug">{message}</p>
        {action ? (
          <button
            type="button"
            onClick={() => {
              action.onClick();
              onDismiss(id);
            }}
            className={cn(
              'mt-1 self-start text-xs underline-offset-2',
              'text-[var(--tvl-raised)] opacity-80 hover:opacity-100 hover:underline',
              'transition-opacity duration-[var(--tvl-d-fast)]',
            )}
          >
            {action.label}
          </button>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
        className={cn(
          'mt-0.5 shrink-0 rounded-[var(--tvl-r1)] p-0.5',
          'text-[var(--tvl-raised)] opacity-60 hover:opacity-100',
          'transition-opacity duration-[var(--tvl-d-fast)]',
        )}
      >
        <CloseLine size={16} color="currentColor" aria-hidden />
      </button>
    </div>
  );
}

export function Toaster() {
  const [items, setItems] = React.useState<ToastEvent[]>([]);

  React.useEffect(() => {
    function onEvent(e: Event) {
      const detail = (e as CustomEvent<ToastEvent>).detail;
      if (!detail) return;
      setItems((prev) => [...prev, detail]);
    }
    window.addEventListener(TOAST_EVENT_NAME, onEvent);
    return () => window.removeEventListener(TOAST_EVENT_NAME, onEvent);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <div
      aria-label="Notifications"
      className={cn(
        'fixed top-0 right-0 z-[var(--tvl-z-toast)] flex max-h-[100dvh] flex-col items-end gap-2',
        'overflow-hidden p-4 pointer-events-none',
        // Clear the status bar and, in landscape, the notch on the right-hand side.
        'pt-[calc(1rem+var(--tvl-safe-top))] pr-[calc(1rem+var(--tvl-safe-right))]',
      )}
    >
      {items.map((item) => (
        <ToastCard key={item.id} event={item} onDismiss={dismiss} />
      ))}
    </div>
  );
}
